import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useCompany } from '../../hooks/useCompany';
import { cashAPI, cashAccountsAPI, accountsAPI } from '../../lib/api';
import { toast } from 'sonner';
import {
  Wallet,
  DollarSign,
  Building2,
  Smartphone,
  PiggyBank,
  Users,
  Plus,
  ArrowUpCircle,
  ArrowDownCircle,
  History,
  RefreshCw,
  X,
  ChevronDown,
  ChevronRight,
  Edit,
  Trash2,
} from 'lucide-react';

const formatCurrency = (value, currency = 'USD') => {
  if (currency === 'LRD') {
    return `L$${new Intl.NumberFormat('en-US').format(value || 0)}`;
  }
  return `$${new Intl.NumberFormat('en-US').format(value || 0)}`;
};

const ACCOUNT_TYPE_CONFIG = {
  teller: { icon: Users, color: 'text-indigo-600', bg: 'bg-indigo-100', darkBg: 'bg-indigo-500', label: 'Tellers' },
  bank: { icon: Building2, color: 'text-blue-600', bg: 'bg-blue-100', darkBg: 'bg-blue-500', label: 'Bank Accounts' },
  petty_cash: { icon: PiggyBank, color: 'text-amber-600', bg: 'bg-amber-100', darkBg: 'bg-amber-500', label: 'Petty Cash' },
  mobile_money: { icon: Smartphone, color: 'text-green-600', bg: 'bg-green-100', darkBg: 'bg-green-500', label: 'Mobile Money' },
  coa: { icon: DollarSign, color: 'text-slate-600', bg: 'bg-slate-100', darkBg: 'bg-slate-500', label: 'Chart of Accounts (Unlinked)' },
};

// Create Cash Account Modal
function CreateCashAccountModal({ isOpen, onClose, onSuccess, companyId, defaultType = 'bank' }) {
  const [name, setName] = useState('');
  const [accountType, setAccountType] = useState(defaultType);
  const [currency, setCurrency] = useState('USD');
  const [initialBalance, setInitialBalance] = useState('');
  const [institutionName, setInstitutionName] = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  const [coaAccountId, setCoaAccountId] = useState('');
  const [description, setDescription] = useState('');

  const { data: coaAccounts = [] } = useQuery({
    queryKey: ['coa-cash-accounts', companyId],
    queryFn: () => accountsAPI.getAll(companyId).then(res => 
      res.data.filter(a => a.account_type === 'asset' && 
        ['bank', 'cash_and_equivalents', 'other_current_asset'].includes(a.detail_type))
    ),
    enabled: isOpen && !!companyId,
  });

  const mutation = useMutation({
    mutationFn: (data) => cashAccountsAPI.create(data),
    onSuccess: () => {
      toast.success('Cash account created successfully');
      onSuccess();
      onClose();
      resetForm();
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Failed to create account');
    },
  });

  const resetForm = () => {
    setName('');
    setAccountType(defaultType);
    setCurrency('USD');
    setInitialBalance('');
    setInstitutionName('');
    setAccountNumber('');
    setCoaAccountId('');
    setDescription('');
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!name) {
      toast.error('Please enter account name');
      return;
    }
    mutation.mutate({
      company_id: companyId,
      name,
      account_type: accountType,
      currency,
      initial_balance: parseFloat(initialBalance) || 0,
      institution_name: institutionName || undefined,
      account_number: accountNumber || undefined,
      coa_account_id: coaAccountId || undefined,
      description: description || undefined,
    });
  };

  if (!isOpen) return null;

  const config = ACCOUNT_TYPE_CONFIG[accountType];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card border border-border rounded-lg w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold flex items-center gap-2">
              {config && <config.icon className={`h-5 w-5 ${config.color}`} />}
              Add {config?.label || 'Cash Account'}
            </h3>
            <button onClick={onClose} className="p-1 hover:bg-muted rounded">
              <X className="h-5 w-5" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">Account Type *</label>
              <select
                value={accountType}
                onChange={(e) => setAccountType(e.target.value)}
                className="w-full px-3 py-2 bg-background border border-border rounded-md"
              >
                <option value="bank">Bank Account</option>
                <option value="petty_cash">Petty Cash</option>
                <option value="mobile_money">Mobile Money</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Account Name *</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={accountType === 'bank' ? 'e.g., Ecobank - Operating' : accountType === 'mobile_money' ? 'e.g., Orange Money Float' : 'e.g., Office Petty Cash'}
                className="w-full px-3 py-2 bg-background border border-border rounded-md"
              />
            </div>

            {accountType === 'bank' && (
              <>
                <div>
                  <label className="block text-sm font-medium mb-1">Bank Name</label>
                  <input
                    type="text"
                    value={institutionName}
                    onChange={(e) => setInstitutionName(e.target.value)}
                    placeholder="e.g., Ecobank, LBDI, GN Bank"
                    className="w-full px-3 py-2 bg-background border border-border rounded-md"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Account Number</label>
                  <input
                    type="text"
                    value={accountNumber}
                    onChange={(e) => setAccountNumber(e.target.value)}
                    placeholder="e.g., 0012345678"
                    className="w-full px-3 py-2 bg-background border border-border rounded-md"
                  />
                </div>
              </>
            )}

            {accountType === 'mobile_money' && (
              <div>
                <label className="block text-sm font-medium mb-1">Provider</label>
                <input
                  type="text"
                  value={institutionName}
                  onChange={(e) => setInstitutionName(e.target.value)}
                  placeholder="e.g., Orange Money, MTN, Lonestar"
                  className="w-full px-3 py-2 bg-background border border-border rounded-md"
                />
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Currency *</label>
                <select
                  value={currency}
                  onChange={(e) => setCurrency(e.target.value)}
                  className="w-full px-3 py-2 bg-background border border-border rounded-md"
                >
                  <option value="USD">USD ($)</option>
                  <option value="LRD">LRD (L$)</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Initial Balance</label>
                <input
                  type="number"
                  value={initialBalance}
                  onChange={(e) => setInitialBalance(e.target.value)}
                  placeholder="0.00"
                  className="w-full px-3 py-2 bg-background border border-border rounded-md"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Link to Chart of Accounts</label>
              <select
                value={coaAccountId}
                onChange={(e) => setCoaAccountId(e.target.value)}
                className="w-full px-3 py-2 bg-background border border-border rounded-md"
              >
                <option value="">Select COA account (optional)</option>
                {coaAccounts.map((acc) => (
                  <option key={acc.id} value={acc.id}>
                    {acc.code} - {acc.name}
                  </option>
                ))}
              </select>
              <p className="text-xs text-muted-foreground mt-1">
                Linking enables automatic double-entry journal entries
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Description</label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Optional notes about this account"
                rows={2}
                className="w-full px-3 py-2 bg-background border border-border rounded-md"
              />
            </div>

            <div className="flex gap-2 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-4 py-2 border border-border rounded-md hover:bg-muted"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={mutation.isPending}
                className="flex-1 px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 disabled:opacity-50"
              >
                {mutation.isPending ? 'Creating...' : 'Create Account'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

// Transaction Modal for Cash Accounts
function TransactionModal({ account, isOpen, onClose, onSuccess, companyId }) {
  const [transactionType, setTransactionType] = useState('deposit');
  const [amount, setAmount] = useState('');
  const [reference, setReference] = useState('');
  const [notes, setNotes] = useState('');
  const [counterpartyAccountId, setCounterpartyAccountId] = useState('');

  const { data: coaAccounts = [] } = useQuery({
    queryKey: ['coa-accounts-for-txn', companyId],
    queryFn: () => accountsAPI.getAll(companyId).then(res => res.data),
    enabled: isOpen && !!companyId,
  });

  const mutation = useMutation({
    mutationFn: (data) => cashAccountsAPI.transaction(account.id, data),
    onSuccess: () => {
      toast.success('Transaction recorded successfully');
      onSuccess();
      onClose();
      setAmount('');
      setReference('');
      setNotes('');
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Transaction failed');
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    const amountNum = parseFloat(amount);
    if (!amountNum || amountNum <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    mutation.mutate({
      cash_account_id: account.id,
      transaction_type: transactionType,
      amount: amountNum,
      reference: reference || undefined,
      notes: notes || undefined,
      counterparty_account_id: counterpartyAccountId || undefined,
    });
  };

  if (!isOpen || !account) return null;

  const isWithdrawal = transactionType === 'withdrawal' || transactionType === 'transfer_out';

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card border border-border rounded-lg w-full max-w-md">
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold">Record Transaction</h3>
            <button onClick={onClose} className="p-1 hover:bg-muted rounded">
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="mb-4 p-3 bg-muted/50 rounded-lg">
            <p className="text-sm font-medium">{account.name}</p>
            <p className="text-xs text-muted-foreground">{account.account_type} • {account.currency}</p>
            <p className="text-lg font-semibold mt-1">{formatCurrency(account.balance, account.currency)}</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">Transaction Type</label>
              <select
                value={transactionType}
                onChange={(e) => setTransactionType(e.target.value)}
                className="w-full px-3 py-2 bg-background border border-border rounded-md"
              >
                <option value="deposit">Deposit (Add Funds)</option>
                <option value="withdrawal">Withdrawal (Remove Funds)</option>
                <option value="transfer_in">Transfer In</option>
                <option value="transfer_out">Transfer Out</option>
                <option value="adjustment">Balance Adjustment</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Amount ({account.currency}) *</label>
              <input
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="0.00"
                className="w-full px-3 py-2 bg-background border border-border rounded-md"
              />
              {isWithdrawal && parseFloat(amount) > account.balance && (
                <p className="text-xs text-red-500 mt-1">Exceeds available balance</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Counterparty Account (for Journal Entry)</label>
              <select
                value={counterpartyAccountId}
                onChange={(e) => setCounterpartyAccountId(e.target.value)}
                className="w-full px-3 py-2 bg-background border border-border rounded-md"
              >
                <option value="">Select account (optional)</option>
                {coaAccounts.map((acc) => (
                  <option key={acc.id} value={acc.id}>
                    {acc.code} - {acc.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Reference</label>
              <input
                type="text"
                value={reference}
                onChange={(e) => setReference(e.target.value)}
                placeholder="e.g., Receipt #, Check #"
                className="w-full px-3 py-2 bg-background border border-border rounded-md"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Notes</label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Additional details"
                rows={2}
                className="w-full px-3 py-2 bg-background border border-border rounded-md"
              />
            </div>

            <div className="flex gap-2 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-4 py-2 border border-border rounded-md hover:bg-muted"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={mutation.isPending || (isWithdrawal && parseFloat(amount) > account.balance)}
                className="flex-1 px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 disabled:opacity-50"
              >
                {mutation.isPending ? 'Processing...' : 'Record Transaction'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

// Expandable Category Section
function CategorySection({ type, label, sources, cashAccounts, onAddAccount, onRecordTransaction, onRefresh }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const config = ACCOUNT_TYPE_CONFIG[type];
  const Icon = config?.icon || Wallet;
  
  const accounts = sources?.filter(s => s.type === type) || [];
  const totalUsd = accounts.reduce((sum, a) => sum + (a.usd || 0), 0);
  const totalLrd = accounts.reduce((sum, a) => sum + (a.lrd || 0), 0);

  const isCashAccountType = ['bank', 'petty_cash', 'mobile_money'].includes(type);

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden">
      {/* Header - Clickable */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full p-4 flex items-center justify-between hover:bg-muted/30 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className={`p-2 ${config?.bg || 'bg-gray-100'} rounded-lg`}>
            <Icon className={`h-5 w-5 ${config?.color || 'text-gray-600'}`} />
          </div>
          <div className="text-left">
            <h3 className="font-semibold">{label}</h3>
            <p className="text-sm text-muted-foreground">{accounts.length} account(s)</p>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-right">
            <p className="font-semibold text-green-600">{formatCurrency(totalUsd, 'USD')}</p>
            <p className="text-sm text-blue-600">{formatCurrency(totalLrd, 'LRD')}</p>
          </div>
          {isExpanded ? (
            <ChevronDown className="h-5 w-5 text-muted-foreground" />
          ) : (
            <ChevronRight className="h-5 w-5 text-muted-foreground" />
          )}
        </div>
      </button>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="border-t border-border">
          {/* Actions Bar */}
          {isCashAccountType && (
            <div className="p-3 bg-muted/30 border-b border-border flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Manage {label.toLowerCase()}</span>
              <button
                onClick={() => onAddAccount(type)}
                className="flex items-center gap-1 px-3 py-1.5 text-sm bg-primary text-primary-foreground rounded-md hover:bg-primary/90"
              >
                <Plus className="h-4 w-4" />
                Add {type === 'bank' ? 'Bank Account' : type === 'petty_cash' ? 'Petty Cash' : 'Mobile Money'}
              </button>
            </div>
          )}

          {/* Account List */}
          <div className="divide-y divide-border">
            {accounts.length === 0 ? (
              <div className="p-6 text-center text-muted-foreground">
                <Icon className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p>No {label.toLowerCase()} accounts found</p>
                {isCashAccountType && (
                  <button
                    onClick={() => onAddAccount(type)}
                    className="mt-2 text-sm text-primary hover:underline"
                  >
                    Add your first {type.replace('_', ' ')} account
                  </button>
                )}
              </div>
            ) : (
              accounts.map((account) => {
                const cashAccount = cashAccounts?.find(a => a.id === account.id);
                return (
                  <div key={account.id} className="p-4 hover:bg-muted/20 transition-colors">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`p-1.5 ${config?.bg || 'bg-gray-100'} rounded`}>
                          <Icon className={`h-4 w-4 ${config?.color || 'text-gray-600'}`} />
                        </div>
                        <div>
                          <p className="font-medium">{account.name}</p>
                          {account.institution_name && (
                            <p className="text-xs text-muted-foreground">{account.institution_name}</p>
                          )}
                          {account.account_number && (
                            <p className="text-xs text-muted-foreground">Acct: ****{account.account_number.slice(-4)}</p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <p className={`font-mono font-semibold ${account.currency === 'USD' ? 'text-green-600' : 'text-blue-600'}`}>
                            {formatCurrency(account.balance, account.currency)}
                          </p>
                          {account.coa_account_code && (
                            <p className="text-xs text-primary">COA: {account.coa_account_code}</p>
                          )}
                        </div>
                        {isCashAccountType && cashAccount && (
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => onRecordTransaction(cashAccount)}
                              className="p-1.5 hover:bg-muted rounded text-primary"
                              title="Record Transaction"
                            >
                              <Plus className="h-4 w-4" />
                            </button>
                            <button
                              className="p-1.5 hover:bg-muted rounded text-muted-foreground"
                              title="View History"
                            >
                              <History className="h-4 w-4" />
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function CashPosition() {
  const { selectedCompany, getDFSCompany } = useCompany();
  const queryClient = useQueryClient();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createModalType, setCreateModalType] = useState('bank');
  const [selectedAccountForTxn, setSelectedAccountForTxn] = useState(null);

  const dfsCompany = getDFSCompany ? getDFSCompany() : selectedCompany;
  const companyId = dfsCompany?.id;

  const { data: cashPosition, isLoading } = useQuery({
    queryKey: ['cash-position', companyId],
    queryFn: () => cashAPI.getCashPosition(companyId).then(res => res.data),
    enabled: !!companyId,
    refetchInterval: 30000,
  });

  const { data: cashAccounts = [] } = useQuery({
    queryKey: ['cash-accounts', companyId],
    queryFn: () => cashAccountsAPI.getAll(companyId).then(res => res.data),
    enabled: !!companyId,
  });

  const handleRefresh = () => {
    queryClient.invalidateQueries(['cash-position', companyId]);
    queryClient.invalidateQueries(['cash-accounts', companyId]);
  };

  const handleAddAccount = (type) => {
    setCreateModalType(type);
    setShowCreateModal(true);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="cash-position-page">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">Cash Position</h1>
          <p className="text-muted-foreground text-sm mt-1">
            Consolidated view of all cash and cash-equivalent accounts
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={handleRefresh}
            className="flex items-center gap-2 px-3 py-2 border border-border rounded-md hover:bg-muted"
          >
            <RefreshCw className="h-4 w-4" />
            Refresh
          </button>
        </div>
      </div>

      {/* Grand Total Cards */}
      <div className="grid gap-4 md:grid-cols-2">
        <div className="bg-gradient-to-br from-green-500 to-green-600 text-white rounded-xl p-6">
          <div className="flex items-center gap-2 mb-2">
            <DollarSign className="h-5 w-5" />
            <span className="text-green-100">Total Cash Position (USD)</span>
          </div>
          <p className="text-4xl font-bold">{formatCurrency(cashPosition?.total_usd || 0, 'USD')}</p>
          <div className="mt-4 grid grid-cols-3 sm:grid-cols-5 gap-2 text-xs">
            <div className="bg-white/10 rounded p-2">
              <p className="text-green-100">Tellers</p>
              <p className="font-semibold">{formatCurrency(cashPosition?.teller_usd || 0, 'USD')}</p>
            </div>
            <div className="bg-white/10 rounded p-2">
              <p className="text-green-100">Bank</p>
              <p className="font-semibold">{formatCurrency(cashPosition?.bank_usd || 0, 'USD')}</p>
            </div>
            <div className="bg-white/10 rounded p-2">
              <p className="text-green-100">Petty</p>
              <p className="font-semibold">{formatCurrency(cashPosition?.petty_cash_usd || 0, 'USD')}</p>
            </div>
            <div className="bg-white/10 rounded p-2">
              <p className="text-green-100">Mobile</p>
              <p className="font-semibold">{formatCurrency(cashPosition?.mobile_money_usd || 0, 'USD')}</p>
            </div>
            <div className="bg-white/10 rounded p-2">
              <p className="text-green-100">COA</p>
              <p className="font-semibold">{formatCurrency(cashPosition?.coa_cash_usd || 0, 'USD')}</p>
            </div>
          </div>
        </div>
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 text-white rounded-xl p-6">
          <div className="flex items-center gap-2 mb-2">
            <Wallet className="h-5 w-5" />
            <span className="text-blue-100">Total Cash Position (LRD)</span>
          </div>
          <p className="text-4xl font-bold">{formatCurrency(cashPosition?.total_lrd || 0, 'LRD')}</p>
          <div className="mt-4 grid grid-cols-3 sm:grid-cols-5 gap-2 text-xs">
            <div className="bg-white/10 rounded p-2">
              <p className="text-blue-100">Tellers</p>
              <p className="font-semibold">{formatCurrency(cashPosition?.teller_lrd || 0, 'LRD')}</p>
            </div>
            <div className="bg-white/10 rounded p-2">
              <p className="text-blue-100">Bank</p>
              <p className="font-semibold">{formatCurrency(cashPosition?.bank_lrd || 0, 'LRD')}</p>
            </div>
            <div className="bg-white/10 rounded p-2">
              <p className="text-blue-100">Petty</p>
              <p className="font-semibold">{formatCurrency(cashPosition?.petty_cash_lrd || 0, 'LRD')}</p>
            </div>
            <div className="bg-white/10 rounded p-2">
              <p className="text-blue-100">Mobile</p>
              <p className="font-semibold">{formatCurrency(cashPosition?.mobile_money_lrd || 0, 'LRD')}</p>
            </div>
            <div className="bg-white/10 rounded p-2">
              <p className="text-blue-100">COA</p>
              <p className="font-semibold">{formatCurrency(cashPosition?.coa_cash_lrd || 0, 'LRD')}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Expandable Category Sections */}
      <div className="space-y-3">
        <CategorySection
          type="teller"
          label="Teller Accounts"
          sources={cashPosition?.sources}
          cashAccounts={cashAccounts}
          onAddAccount={handleAddAccount}
          onRecordTransaction={setSelectedAccountForTxn}
          onRefresh={handleRefresh}
        />
        <CategorySection
          type="bank"
          label="Bank Accounts"
          sources={cashPosition?.sources}
          cashAccounts={cashAccounts}
          onAddAccount={handleAddAccount}
          onRecordTransaction={setSelectedAccountForTxn}
          onRefresh={handleRefresh}
        />
        <CategorySection
          type="petty_cash"
          label="Petty Cash"
          sources={cashPosition?.sources}
          cashAccounts={cashAccounts}
          onAddAccount={handleAddAccount}
          onRecordTransaction={setSelectedAccountForTxn}
          onRefresh={handleRefresh}
        />
        <CategorySection
          type="mobile_money"
          label="Mobile Money"
          sources={cashPosition?.sources}
          cashAccounts={cashAccounts}
          onAddAccount={handleAddAccount}
          onRecordTransaction={setSelectedAccountForTxn}
          onRefresh={handleRefresh}
        />
        <CategorySection
          type="coa"
          label="Chart of Accounts (Unlinked)"
          sources={cashPosition?.sources}
          cashAccounts={cashAccounts}
          onAddAccount={handleAddAccount}
          onRecordTransaction={setSelectedAccountForTxn}
          onRefresh={handleRefresh}
        />
      </div>

      {/* Modals */}
      <CreateCashAccountModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={handleRefresh}
        companyId={companyId}
        defaultType={createModalType}
      />

      {selectedAccountForTxn && (
        <TransactionModal
          account={selectedAccountForTxn}
          isOpen={!!selectedAccountForTxn}
          onClose={() => setSelectedAccountForTxn(null)}
          onSuccess={handleRefresh}
          companyId={companyId}
        />
      )}
    </div>
  );
}
